import { z } from 'zod';
import { isoTimestamp } from './common';

/**
 * Files and images attached to messages. Only these types are accepted; the server checks the
 * actual bytes, never just the declared type. SVG is excluded (it can carry scripts).
 */
export const IMAGE_MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export const DOCUMENT_MEDIA_TYPES = ['application/pdf'] as const;
export const TEXT_MEDIA_TYPES = [
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
] as const;
export const ATTACHMENT_MEDIA_TYPES = [
  ...IMAGE_MEDIA_TYPES,
  ...DOCUMENT_MEDIA_TYPES,
  ...TEXT_MEDIA_TYPES,
] as const;
export const attachmentMediaTypeSchema = z.enum(ATTACHMENT_MEDIA_TYPES);
export type AttachmentMediaType = z.infer<typeof attachmentMediaTypeSchema>;

export const isImageType = (t: string) => (IMAGE_MEDIA_TYPES as readonly string[]).includes(t);
export const isTextType = (t: string) => (TEXT_MEDIA_TYPES as readonly string[]).includes(t);

/** What the file picker accepts (extensions help browsers that report no type). */
export const ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/markdown,text/csv,application/json,.md,.csv,.json,.txt';

export const MAX_ATTACHMENTS_PER_MESSAGE = 10;
export const DEFAULT_FILE_MAX_BYTES = 10 * 1024 * 1024;

export const fileNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(255)
  // No path separators or control characters (checked by code point, not a control-char regex).
  .refine((n) => !/[\\/]/.test(n) && [...n].every((c) => c.charCodeAt(0) >= 0x20), {
    message: 'Invalid file name',
  });

/** A stored file referenced by a message. */
export const attachmentSchema = z.object({
  fileId: z.uuid(),
  name: fileNameSchema,
  mediaType: attachmentMediaTypeSchema,
  sizeBytes: z.number().int().nonnegative(),
});
export type Attachment = z.infer<typeof attachmentSchema>;

export const fileDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  mediaType: attachmentMediaTypeSchema,
  sizeBytes: z.number().int(),
  sha256: z.string(),
  createdAt: isoTimestamp,
});
export type FileDto = z.infer<typeof fileDtoSchema>;

/** Gateway messages may carry a file inline (base64) instead of an uploaded file id. */
export const inlineAttachmentSchema = z.object({
  name: fileNameSchema,
  mediaType: attachmentMediaTypeSchema,
  /** Base64 (standard or URL-safe; a `data:` URL prefix is accepted). */
  data: z.string().min(1),
});
export type InlineAttachment = z.infer<typeof inlineAttachmentSchema>;

export const gatewayAttachmentSchema = z.union([
  z.object({ fileId: z.uuid() }),
  inlineAttachmentSchema,
]);
export type GatewayAttachment = z.infer<typeof gatewayAttachmentSchema>;

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
